import axios from 'axios';
import { baseUrl } from '../hooks/useEndpoints';
import type {
  ITrainingStatistics,
  ISportTaxonomy,
  IClassificationSuggestion,
  TrainingStatsPeriodType,
} from '../models/trainingStatistics';

/**
 * Endpoints modulo Training Statistics + Classification Suggest.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md Sez 20-21.
 */

// ─── Training Statistics ───────────────────────────────────────────────────

export const trainingStatsEndpoints = {
  /** GET /training-statistics/team/:teamId */
  getTeamStats: async (
    teamId: string,
    periodType: TrainingStatsPeriodType,
    periodStart: string,
    periodEnd: string,
  ): Promise<ITrainingStatistics> => {
    const res = await axios.get<ITrainingStatistics>(
      `${baseUrl}/training-statistics/team/${teamId}`,
      {
        params: { periodType, periodStart, periodEnd },
      },
    );
    return res.data;
  },

  /** POST /training-statistics/recalculate */
  forceRecalculate: async (
    teamId: string,
    periodType: TrainingStatsPeriodType,
    periodStart: string,
    periodEnd: string,
  ): Promise<ITrainingStatistics> => {
    const res = await axios.post<ITrainingStatistics>(
      `${baseUrl}/training-statistics/recalculate`,
      { teamId, periodType, periodStart, periodEnd },
    );
    return res.data;
  },

  /** GET /training-statistics/correlation/:teamId?periodStart=&periodEnd= */
  getCorrelation: async (
    teamId: string,
    periodStart: string,
    periodEnd: string,
  ): Promise<{
    correlations: Array<{
      objectiveId: string;
      objectiveMinutes: number;
      matchesCount: number;
      avgOutcome: string | null;
      relatedStats: Record<string, number>;
    }>;
  }> => {
    const res = await axios.get(
      `${baseUrl}/training-statistics/correlation/${teamId}`,
      { params: { periodStart, periodEnd } },
    );
    return res.data;
  },
};

// ─── Sport Taxonomies ──────────────────────────────────────────────────────

export const sportTaxonomiesEndpoints = {
  /** GET /sport-taxonomies?sport=soccer&language=it */
  getBySport: async (sport: string, language = 'it'): Promise<ISportTaxonomy[]> => {
    const res = await axios.get<ISportTaxonomy[]>(`${baseUrl}/sport-taxonomies`, {
      params: { sport, language },
    });
    return res.data;
  },

  /** POST /sport-taxonomies/suggest-classification */
  suggestClassification: async (payload: {
    exerciseName?: string;
    tags?: string[];
    notes?: string;
    sportType: string;
    blockOrder?: number;
    totalBlocks?: number;
    durationMinutes?: number;
  }): Promise<IClassificationSuggestion> => {
    const res = await axios.post<IClassificationSuggestion>(
      `${baseUrl}/sport-taxonomies/suggest-classification`,
      payload,
    );
    return res.data;
  },

  /** POST /sport-taxonomies/classification-feedback */
  sendFeedback: async (payload: {
    blockId?: string;
    sport: string;
    chosenSessionPhaseId?: string;
    chosenDidacticMethodId?: string;
    suggestedSessionPhaseId?: string;
    suggestedDidacticMethodId?: string;
    accepted: boolean;
  }): Promise<void> => {
    await axios.post(`${baseUrl}/sport-taxonomies/classification-feedback`, payload);
  },
};
